/**
 * Validation des champs personnalisés des formulaires PUBLICS.
 *
 * SOURCE UNIQUE partagée par l'adhésion (`/a/[slug]`) et la billetterie
 * (`/b/[slug]`) : sans elle, les deux parcours divergeaient au premier
 * ajustement — c'est déjà arrivé entre le dashboard et le mobile.
 *
 * Chaque règle renvoie un message DESTINÉ À L'UTILISATEUR, affiché sous le
 * champ concerné (Karim 2026-09-21 : « une vraie gestion d'erreur »). On dit
 * ce qui ne va pas ET ce qu'on attend — « Format attendu : 06 12 34 56 78 »
 * vaut mieux que « Champ invalide ».
 */
import { isValidEmailStrict } from "@donoor/validators";
import { messageTelephoneInvalide, telephoneValide } from "@donoor/utils";
import { dateIsoExiste } from "~/utils/dateSaisie";

/** Type de champ tel que stocké (adhésion : `field_type`, billetterie : `type`). */
export type TypeChampPublic =
  | "text"
  | "textarea"
  | "email"
  | "phone"
  | "number"
  | "date"
  | "select"
  | "checkbox"
  | "boolean"
  | "file"
  | "address";

export interface ChampAValider {
  id: string;
  label: string;
  type: TypeChampPublic;
  requis: boolean;
}

/**
 * Le champ est-il VIDE ?
 *
 * ⚠️ `false` n'est PAS vide pour une case à cocher : décocher est une réponse.
 * Seule une case REQUISE doit être cochée (consentement), ce que gère l'appelant.
 */

/**
 * Traduction hors composant : via l'instance i18n de Nuxt, résolue à chaque
 * appel → suit la langue de la page (cf. docs/architecture/i18n.md).
 */
function t(cle: string): string {
  return useNuxtApp().$i18n.t(cle);
}
function estVide(valeur: unknown): boolean {
  if (valeur === undefined || valeur === null) return true;
  if (typeof valeur === "string") return valeur.trim() === "";
  if (typeof valeur === "object") {
    // Fichier uploadé : objet `{ file_id, file_name }`.
    return Object.keys(valeur as Record<string, unknown>).length === 0;
  }
  return false;
}



/**
 * Valide UN champ. Renvoie le message d'erreur, ou `null` si tout va bien.
 *
 * Un champ FACULTATIF laissé vide est valide — mais s'il est rempli, son
 * format est vérifié : une adresse email facultative mal tapée est une adresse
 * perdue, pas une case qu'on ignore.
 */
export function validerChamp(
  champ: ChampAValider,
  valeur: unknown,
  /** Pays de l'association : un numéro tapé sans indicatif s'y lit. */
  paysAsso = "FR",
): string | null {
  const vide = estVide(valeur);

  if (champ.requis) {
    // Case à cocher requise = consentement : elle doit être COCHÉE.
    if (champ.type === "checkbox" || champ.type === "boolean") {
      return valeur === true ? null : t("association.champsFormulaire.caseObligatoire");
    }
    // Choix multiple (réponse = tableau) : au moins une case.
    if (Array.isArray(valeur) && valeur.length === 0) return t("association.champsFormulaire.auMoinsUneReponse");
    if (vide) return t("association.champsFormulaire.champObligatoire");
  }

  if (vide) return null; // facultatif et vide : rien à vérifier

  switch (champ.type) {
    case "email":
      return isValidEmailStrict(String(valeur).trim())
        ? null
        : t("association.champsFormulaire.emailInvalide");

    case "phone":
      // Règle partagée (@donoor/utils) : numéro réel du pays de l'asso, ou
      // de n'importe quel pays s'il est tapé avec son indicatif.
      return telephoneValide(String(valeur), paysAsso) ? null : messageTelephoneInvalide(paysAsso);

    case "number":
      return Number.isFinite(Number(valeur))
        ? null
        : t("association.champsFormulaire.nombreAttendu");

    case "date":
      // Sans fuseau horaire : en France, l'ancienne vérification refusait
      // TOUTE date (cf. dateSaisie.ts, Karim 2026-09-29).
      return dateIsoExiste(String(valeur))
        ? null
        : "Date invalide. Format attendu : JJ/MM/AAAA";

    case "address":
      // Une adresse tapée trop courte n'en est pas une ; on ne peut pas exiger
      // qu'elle vienne des suggestions (l'étranger n'y est pas).
      return String(valeur).trim().length >= 5
        ? null
        : t("association.champsFormulaire.adresseTropCourte");

    default:
      return null;
  }
}

/**
 * Valide une LISTE de champs et renvoie les erreurs par identifiant.
 * Un objet vide signifie « tout est bon ».
 */
export function validerChamps(
  champs: ChampAValider[],
  valeurs: Record<string, unknown>,
  paysAsso = "FR",
): Record<string, string> {
  const erreurs: Record<string, string> = {};
  for (const champ of champs) {
    const message = validerChamp(champ, valeurs[champ.id], paysAsso);
    if (message) erreurs[champ.id] = message;
  }
  return erreurs;
}

/**
 * Amène l'utilisateur SUR le premier champ fautif.
 *
 * Sans ça, un formulaire long affiche une erreur hors écran et le bouton
 * semble simplement ne « rien faire » — le reproche exact de Karim.
 * `scrollIntoView` centre le champ, puis on lui donne le focus.
 */
export function allerAuPremierChampEnErreur(
  erreurs: Record<string, string>,
  prefixeId = "champ",
): void {
  const premier = Object.keys(erreurs)[0];
  if (!premier) return;
  const el = document.getElementById(`${prefixeId}-${premier}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Le focus attend la fin du défilement : le poser tout de suite annule
  // l'animation dans Safari.
  setTimeout(() => {
    (el as HTMLElement).focus?.();
  }, 300);
}
